import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Menu,
  Button,
  Skeleton,
} from "@mui/material";
import {
  IconClose24,
  IconSearch16,
  IconChevronDown16,
} from "./TicketV2Icons";
import {
  getCustomerConversationHistory,
  resetCustomerConversationHistory,
} from "../../../store/features/getCustomerConversationHistory";
import {
  TICKETS_HOME_PATH,
  LIVE_CHAT_HOME_PATH,
} from "../../../config/featureFlags";

const inter = '"Inter", sans-serif';
const PAGE_SIZE = 10;

const CHANNEL_OPTIONS = [
  { id: "all", label: "All Channel", apiType: null },
  { id: "ticket", label: "Ticket", apiType: "ticket" },
  { id: "livechat", label: "Live Chat", apiType: "live_chat" },
];

const STATUS_OPTIONS = [
  { id: "all", label: "All Status", apiStatus: null },
  { id: "open", label: "Open", apiStatus: "open" },
  { id: "pending", label: "Pending", apiStatus: "pending" },
  { id: "closed", label: "Closed", apiStatus: "closed" },
];

const filterBtnSx = {
  height: 40,
  minWidth: "fit-content",
  px: 1.5,
  borderRadius: "8px",
  border: "1px solid #D5DCE1",
  bgcolor: "#FFF",
  boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
  color: "#405261",
  fontFamily: inter,
  fontSize: 14,
  fontWeight: 400,
  textTransform: "none",
  lineHeight: "18px",
  whiteSpace: "nowrap",
  gap: 0.5,
  flexShrink: 0,
  "&:hover": { bgcolor: "#F9FAFA", borderColor: "#D5DCE1" },
  "& .MuiButton-endIcon": {
    m: 0,
    ml: 0.5,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
};

const filterMenuPaperSx = {
  mt: 0.5,
  minWidth: 148,
  borderRadius: "8px",
  border: "1px solid #D5DCE1",
  boxShadow: "0 8px 24px rgba(6, 21, 34, 0.08)",
};

/** Modal sits at 10001 — menus must render above it. */
const filterMenuSlotProps = {
  root: { sx: { zIndex: 10050 } },
  paper: { sx: filterMenuPaperSx },
};

const pagerBtnSx = {
  height: 40,
  minWidth: 88,
  px: 2,
  borderRadius: "8px",
  border: "1px solid #D5DCE1",
  bgcolor: "#FFF",
  boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
  color: "#405261",
  fontFamily: inter,
  fontSize: 14,
  fontWeight: 500,
  textTransform: "none",
  "&:hover": { bgcolor: "#F9FAFA", borderColor: "#D5DCE1" },
  "&.Mui-disabled": {
    color: "#A5B2BD",
    borderColor: "#E5E9EC",
    bgcolor: "#FFF",
  },
};

const COLS = [
  { key: "conversation", label: "Conversation", flex: "1 1 280px", minWidth: 200 },
  { key: "type", label: "Type", width: 96 },
  { key: "status", label: "Status", width: 88 },
  { key: "assignedTo", label: "Assigned to", width: 128 },
  { key: "number", label: "Number", width: 80 },
  { key: "lastActivity", label: "Last Activity", width: 104 },
];

function ConversationsTableSkeleton({ rows = 5}) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <Stack
          key={i}
          direction="row"
          alignItems="center"
          sx={{
            borderBottom: "1px solid #E5E9EC",
            px: 3,
            py: 1.5,
            minHeight: 64,
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ flex: "1 1 280px", minWidth: 200, pr: 1 }}>
            <Skeleton
              variant="rounded"
              height={16}
              sx={{ mb: 0.75, borderRadius: "4px", maxWidth: i % 2 === 0 ? "88%" : "72%" }}
            />
            <Skeleton
              variant="rounded"
              height={12}
              sx={{ borderRadius: "4px", maxWidth: i % 3 === 0 ? "70%" : "55%" }}
            />
          </Box>
          <Box sx={{ width: 96, flexShrink: 0 }}>
            <Skeleton variant="rounded" width={56} height={14} sx={{ borderRadius: "4px" }} />
          </Box>
          <Box sx={{ width: 88, flexShrink: 0 }}>
            <Skeleton variant="rounded" width={64} height={24} sx={{ borderRadius: "16px" }} />
          </Box>
          <Box sx={{ width: 128, flexShrink: 0 }}>
            <Skeleton variant="rounded" width={96} height={14} sx={{ borderRadius: "4px" }} />
          </Box>
          <Box sx={{ width: 80, flexShrink: 0 }}>
            <Skeleton variant="rounded" width={48} height={14} sx={{ borderRadius: "4px" }} />
          </Box>
          <Box sx={{ width: 104, flexShrink: 0 }}>
            <Skeleton variant="rounded" width={64} height={14} sx={{ borderRadius: "4px" }} />
          </Box>
        </Stack>
      ))}
    </>
  );
}

/**
 * Figma: Conversations history modal (Tickets frame 258).
 * Opens from Ticket Details → History → "View All".
 * Loads via GET /customer/history/:customerId
 */
function ConversationsHistoryModal({
  isOpen,
  onClose,
  customerId = "",
  customerName = "Customer",
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const history = useSelector((s) => s.customerConversationHistory);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [channel, setChannel] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [channelMenuAnchor, setChannelMenuAnchor] = useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setChannelMenuAnchor(null);
      setStatusMenuAnchor(null);
      setSearch("");
      setDebouncedSearch("");
      setChannel("all");
      setStatus("all");
      setPage(1);
      dispatch(resetCustomerConversationHistory());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, channel, status]);

  useEffect(() => {
    if (!isOpen || !customerId) return undefined;
    const channelOpt = CHANNEL_OPTIONS.find((o) => o.id === channel);
    const statusOpt = STATUS_OPTIONS.find((o) => o.id === status);
    dispatch(
      getCustomerConversationHistory({
        customerId,
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        type: channelOpt?.apiType || undefined,
        status: statusOpt?.apiStatus || undefined,
      })
    );
  }, [isOpen, customerId, page, debouncedSearch, channel, status, dispatch]);

  const openConversation = useCallback(
    (row) => {
      if (!row?.id) return;
      const typeKey = String(row.typeKey || "").toLowerCase();
      const statusKey = String(row.statusKey || row.status || "").toLowerCase();
      onClose?.();
      if (typeKey === "live_chat" || typeKey === "livechat" || typeKey === "chat") {
        const isArchived =
          statusKey === "closed" ||
          statusKey === "ended" ||
          statusKey === "archive" ||
          statusKey === "archived";
        const params = new URLSearchParams();
        params.set("roomId", row.id);
        if (isArchived) params.set("tab", "closed");
        navigate(`${LIVE_CHAT_HOME_PATH}?${params.toString()}`);
        return;
      }
      // Default: ticket conversation
      navigate(`${TICKETS_HOME_PATH}?ticketId=${encodeURIComponent(row.id)}`);
    },
    [navigate, onClose]
  );

  const channelLabel =
    CHANNEL_OPTIONS.find((o) => o.id === channel)?.label || "All Channel";
  const statusLabel =
    STATUS_OPTIONS.find((o) => o.id === status)?.label || "All Status";

  const rows = history?.rows || [];
  const pagination = history?.pagination || {};
  const total = pagination.total ?? history?.summary?.total ?? 0;
  const totalPages = Math.max(1, pagination.total_pages || 1);
  const from = pagination.from ?? (rows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1);
  const to = pagination.to ?? Math.min(total, page * PAGE_SIZE);
  const summaryLabel =
    history?.summary?.label ||
    `${total} previous conversations across tickets and live chat`;
  const displayName = history?.customer?.name || customerName || "Customer";
  const loading = Boolean(history?.loading);

  if (!isOpen) return null;

  return (
    <>
      <Box
        className="overlay"
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 10000,
          bgcolor: "rgba(255,255,255,0.6)",
          backdropFilter: "blur(5px)",
        }}
      />
      <Box
        role="dialog"
        aria-modal="true"
        aria-label={`Conversations with ${displayName}`}
        sx={{
          position: "fixed",
          zIndex: 10001,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 936,
          maxWidth: "calc(100vw - 48px)",
          height: 608,
          maxHeight: "calc(100vh - 48px)",
          bgcolor: "#FFF",
          border: "1px solid #D5DCE1",
          borderRadius: "8px",
          boxShadow: "0px 15px 20px 0px rgba(6, 21, 34, 0.05)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxSizing: "border-box",
          fontFamily: inter,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ px: 3, pt: 3, pb: 2, flexShrink: 0 }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: inter,
                fontSize: 18,
                fontWeight: 500,
                color: "#061522",
                lineHeight: "18px",
              }}
            >
              Conversations with {displayName}
            </Typography>
            <Typography
              sx={{
                mt: 1,
                fontFamily: inter,
                fontSize: 12,
                fontWeight: 400,
                color: "#405261",
                lineHeight: "18px",
              }}
            >
              {summaryLabel}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ p: 0, width: 24, height: 24, mt: "-2px" }}
          >
            <IconClose24 />
          </IconButton>
        </Stack>

        <Box sx={{ borderTop: "1px solid #E5E9EC", flexShrink: 0 }} />

        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{ px: 3, py: 2, flexShrink: 0, flexWrap: "wrap" }}
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ..."
            size="small"
            sx={{
              width: 296,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                height: 40,
                borderRadius: "8px",
                fontFamily: inter,
                fontSize: 14,
                bgcolor: "#FFF",
                boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                "& fieldset": { borderColor: "#D5DCE1" },
                "&:hover fieldset": { borderColor: "#D5DCE1" },
                "&.Mui-focused fieldset": { borderColor: "#D5DCE1", borderWidth: 1 },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#A5B2BD",
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, display: "flex" }}>
                    <IconSearch16 />
                  </Box>
                </InputAdornment>
              ),
            }}
          />
          <Button
            disableElevation
            onClick={(e) => setChannelMenuAnchor(e.currentTarget)}
            aria-haspopup="true"
            aria-expanded={Boolean(channelMenuAnchor)}
            endIcon={
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transform: channelMenuAnchor ? "rotate(180deg)" : "none",
                  transition: "transform 0.15s ease",
                }}
              >
                <IconChevronDown16 />
              </Box>
            }
            sx={filterBtnSx}
          >
            {channelLabel}
          </Button>
          <Menu
            anchorEl={channelMenuAnchor}
            open={Boolean(channelMenuAnchor)}
            onClose={() => setChannelMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={filterMenuSlotProps}
          >
            {CHANNEL_OPTIONS.map((option) => (
              <MenuItem
                key={option.id}
                selected={option.id === channel}
                onClick={() => {
                  setChannel(option.id);
                  setChannelMenuAnchor(null);
                }}
                sx={{ fontSize: 14, fontFamily: inter, color: "#061522" }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          <Button
            disableElevation
            onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
            aria-haspopup="true"
            aria-expanded={Boolean(statusMenuAnchor)}
            endIcon={
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transform: statusMenuAnchor ? "rotate(180deg)" : "none",
                  transition: "transform 0.15s ease",
                }}
              >
                <IconChevronDown16 />
              </Box>
            }
            sx={filterBtnSx}
          >
            {statusLabel}
          </Button>
          <Menu
            anchorEl={statusMenuAnchor}
            open={Boolean(statusMenuAnchor)}
            onClose={() => setStatusMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={filterMenuSlotProps}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem
                key={option.id}
                selected={option.id === status}
                onClick={() => {
                  setStatus(option.id);
                  setStatusMenuAnchor(null);
                }}
                sx={{ fontSize: 14, fontFamily: inter, color: "#061522" }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", position: "relative" }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              borderBottom: "1px solid #E5E9EC",
              px: 3,
              py: 1,
              position: "sticky",
              top: 0,
              bgcolor: "#FFF",
              zIndex: 1,
            }}
          >
            {COLS.map((col) => (
              <Typography
                key={col.key}
                sx={{
                  flex: col.flex || "0 0 auto",
                  width: col.width,
                  minWidth: col.minWidth || col.width,
                  fontFamily: inter,
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#405261",
                  lineHeight: "18px",
                }}
              >
                {col.label}
              </Typography>
            ))}
          </Stack>

          {loading ? (
            <ConversationsTableSkeleton rows={6} />
          ) : !customerId ? (
            <Typography
              sx={{
                py: 4,
                px: 3,
                textAlign: "center",
                color: "#8B97A2",
                fontSize: 14,
                fontFamily: inter,
              }}
            >
              No customer linked to this ticket.
            </Typography>
          ) : rows.length === 0 ? (
            <Typography
              sx={{
                py: 4,
                px: 3,
                textAlign: "center",
                color: "#8B97A2",
                fontSize: 14,
                fontFamily: inter,
              }}
            >
              No conversations found.
            </Typography>
          ) : (
            rows.map((row) => (
              <Stack
                key={row.id}
                direction="row"
                alignItems="center"
                role="button"
                tabIndex={0}
                onClick={() => openConversation(row)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openConversation(row);
                  }
                }}
                sx={{
                  borderBottom: "1px solid #E5E9EC",
                  px: 3,
                  py: 1.5,
                  minHeight: 64,
                  boxSizing: "border-box",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#F9FAFA" },
                }}
              >
                <Box sx={{ flex: "1 1 280px", minWidth: 200, pr: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: inter,
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#061522",
                      lineHeight: "18px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.subject}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: inter,
                      fontSize: 12,
                      fontWeight: 400,
                      color: "#405261",
                      lineHeight: "18px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.snippet}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    width: 96,
                    flexShrink: 0,
                    fontFamily: inter,
                    fontSize: 14,
                    color: "#405261",
                    lineHeight: "18px",
                  }}
                >
                  {row.type}
                </Typography>
                <Box sx={{ width: 88, flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 24,
                      px: 1.5,
                      borderRadius: "16px",
                      border: "1px solid #C5CED6",
                      bgcolor: "#F9FAFA",
                      boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: inter,
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#405261",
                        lineHeight: "18px",
                      }}
                    >
                      {row.status}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    width: 128,
                    flexShrink: 0,
                    fontFamily: inter,
                    fontSize: 14,
                    color: "#405261",
                    lineHeight: "18px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.assignedTo}
                </Typography>
                <Typography
                  sx={{
                    width: 80,
                    flexShrink: 0,
                    fontFamily: inter,
                    fontSize: 14,
                    color: "#405261",
                    lineHeight: "18px",
                  }}
                >
                  {row.number}
                </Typography>
                <Typography
                  sx={{
                    width: 104,
                    flexShrink: 0,
                    fontFamily: inter,
                    fontSize: 14,
                    color: "#405261",
                    lineHeight: "18px",
                  }}
                >
                  {row.lastActivity}
                </Typography>
              </Stack>
            ))
          )}
        </Box>

        {total >= PAGE_SIZE && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2, flexShrink: 0, borderTop: "1px solid #E5E9EC" }}
        >
          <Typography
            sx={{
              fontFamily: inter,
              fontSize: 14,
              color: "#405261",
              lineHeight: "18px",
            }}
          >
            Viewing {from}-{to}
          </Typography>
          <Stack direction="row" gap={1.5}>
            <Button
              disableElevation
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              sx={{ ...pagerBtnSx, minWidth: 128 }}
            >
              Previous
            </Button>
            <Button
              disableElevation
              disabled={page >= totalPages || total === 0 || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              sx={pagerBtnSx}
            >
              Next
            </Button>
          </Stack>
        </Stack>
        )}
      </Box>
    </>
  );
}

export default ConversationsHistoryModal;
